import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Seo, Mark, Container, StatBand, VideoSlot } from '../components/marketing/Primitives';
import { useVideo, useContent } from '../cms/ContentProvider';
import {
  reviewSpecimen,
  productionStats,
  psychometrics,
  proofStudy,
  corpus,
  modules,
  qaPipeline,
  qaBar,
  video,
  faq,
} from '../data/suitesData';

/* The interactive specimen: the visitor sits in the reviewer's chair. A
   drafted item arrives triaged, with its designated source passage alongside.
   Approve, revise, or reject, and watch the audit trail record it. The
   classifier ordered the attention; the human decides. */
function ReviewSpecimen() {
  const [decision, setDecision] = useState(null);
  const s = reviewSpecimen;

  const trail = decision ? [...s.baseTrail, s.decisions[decision]] : s.baseTrail;

  return (
    <div className="specimen-card !rotate-0">
      <div className="flex items-baseline justify-between gap-4">
        <p className="text-[11px] font-semibold tracking-wide text-slate">{s.queueLabel}</p>
        <p className="text-[11px] text-faint">You are the expert reviewer</p>
      </div>
      <p className="mt-2 rounded border border-seal/30 bg-seal/5 px-3 py-2 text-[11px] leading-snug text-slate">
        {s.triageNote}
      </p>

      {/* The drafted item */}
      <p className="mt-3 text-[13px] leading-relaxed text-bright">{s.item.stem}</p>
      <ul className="mt-3 space-y-1.5 text-[12px]">
        {s.item.options.map((opt) => (
          <li
            key={opt.key}
            className={`flex gap-2 rounded px-2 py-1 ${
              opt.key === s.item.correctKey ? '-mx-2 bg-seal/10 text-bright' : 'text-slate'
            }`}
          >
            <span className="font-semibold text-bright">{opt.key}.</span>
            <span>{opt.text}</span>
            {opt.key === s.item.correctKey && <span className="ml-auto text-[11px] font-semibold text-seal">key</span>}
          </li>
        ))}
      </ul>

      {/* The source passage the flag points at */}
      <div className="mt-3 rounded border border-hairline bg-ink-deep/60 p-3">
        <p className="text-[10px] font-semibold text-faint">{s.sourceLine}</p>
        <p className="mt-1.5 text-[12px] leading-relaxed text-slate">{s.sourcePassage}</p>
      </div>

      {/* The decision */}
      {!decision ? (
        <div className="mt-4 grid grid-cols-3 gap-2">
          <button type="button" onClick={() => setDecision('approve')} className="btn-primary !px-3 !py-2 text-center text-[12px]">
            Approve
          </button>
          <button
            type="button"
            onClick={() => setDecision('revise')}
            className="rounded border border-hairline px-3 py-2 text-[12px] font-semibold text-bright transition-colors hover:border-seal hover:text-seal"
          >
            Revise
          </button>
          <button
            type="button"
            onClick={() => setDecision('reject')}
            className="rounded border border-hairline px-3 py-2 text-[12px] font-semibold text-slate transition-colors hover:border-rose-400/70 hover:text-rose-300"
          >
            Reject
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setDecision(null)}
          className="mt-4 text-[12px] font-medium text-slate underline decoration-hairline underline-offset-4 hover:text-bright"
        >
          Review it again
        </button>
      )}

      {/* The audit trail */}
      <div className="mt-4 border-t border-hairline pt-3">
        <p className="text-[11px] font-semibold text-bright">Audit trail</p>
        <ol className="mt-2 space-y-2">
          {trail.map((t, i) => (
            <li key={t.event} className="flex gap-2.5">
              <span
                aria-hidden="true"
                className={`mt-1 inline-block h-2 w-2 shrink-0 rounded-full ${
                  i === trail.length - 1 && decision ? 'bg-seal' : 'border border-seal/60'
                }`}
              />
              <div>
                <p className="text-[11px] font-semibold text-slate">{t.event}</p>
                <p className="text-[10px] leading-snug text-faint">{t.detail}</p>
              </div>
            </li>
          ))}
        </ol>
        <p className="mt-3 text-[10px] leading-snug text-faint">
          The classifier ranked the queue and raised the flag. It never gated, and it never decided. You did.
        </p>
      </div>
    </div>
  );
}

// Module screenshots map to these console slots, in module order.
const moduleImageKeys = [
  'suites.generate',
  'suites.itemBank',
  'suites.orals',
  'suites.lms',
  'suites.factCheck',
  'suites.analytics',
  null, // MOC portfolio has no screenshot yet
  'suites.proctoring',
];

export default function SuitesPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const [activeModule, setActiveModule] = useState(0);
  const pipelineVideo = useVideo('suites.pipeline', video.embed ? { embed: video.embed } : null);
  const { images } = useContent();

  const base = modules[activeModule];
  const overrideKey = moduleImageKeys[activeModule];
  const override = overrideKey && images[overrideKey];
  const mod =
    override && override.src && base.shot
      ? { ...base, shot: { ...base.shot, src: override.src } }
      : override && override.src
        ? { ...base, shot: { src: override.src, alt: base.name, caption: base.name } }
        : base;

  return (
    <>
      <Seo
        title="EdAI Suites"
        description="Certification infrastructure, from designated sources to defensible exams: item generation with 87% less expert time, a complete audit trail, LMS, oral exam simulation, MOC portfolio, and secure testing, live in high-stakes examinations since 2025."
      />

      {/* Hero: claim left, the reviewer's chair right. */}
      <section className="overflow-hidden bg-ink text-paper-dark-text">
        <Container className="grid items-start gap-14 py-20 md:py-28 lg:grid-cols-[1fr_1.1fr] lg:gap-12">
          <div className="lg:pt-6">
            <Mark dark>EdAI Suites · For certification boards</Mark>
            <h1 className="hero-rise mt-4 font-serif text-4xl leading-[1.06] md:text-5xl">
              Certification infrastructure, from designated sources to defensible exams.
            </h1>
            <p className="hero-rise-2 mt-6 max-w-measure text-lg leading-relaxed text-slate-dark-text">
              Item generation, learning management, oral examination simulation, MOC portfolio, and secure testing,
              built on your board’s own designated content. Live in high-stakes examinations since 2025.
            </p>
            <p className="hero-rise-2 mt-4 max-w-measure text-sm leading-relaxed text-faint">
              On the right: the reviewer’s chair. A drafted item arrives triaged, with its source passage alongside.
              Decide, and watch the record.
            </p>
            <div className="hero-rise-3 mt-9 flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary-on-dark">Talk to us about your board</Link>
              <a href="#evidence" className="btn-secondary-on-dark">See the evidence</a>
            </div>
          </div>
          <div className="hero-rise-3">
            <ReviewSpecimen />
          </div>
        </Container>
      </section>

      {/* Production stats */}
      <StatBand
        stats={productionStats}
        footnote="From the 2025 production implementation with a US specialty certification board, measured against traditional volunteer item-writing benchmarks."
      />

      {/* The Liability Rule */}
      <section className="py-20 md:py-28">
        <Container>
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
            <div>
              <Mark>The Liability Rule</Mark>
              <h2 className="mt-3 max-w-[22ch] font-serif text-3xl leading-tight md:text-4xl">
                Every consequential decision is made by a human. It is an axiom, not a policy.
              </h2>
              <p className="mt-5 leading-relaxed text-slate">
                Failures in medical AI pipelines are characteristically silent: a wrong answer looks like a right
                answer, and a corrupted corpus looks like a clean one. The human reviewer is the only reliable detector
                for the failure class that matters most. So classifiers and automated checks triage and order expert
                attention, and never gate or decide. The chair who must defend an examination keeps the authority to
                defend it.
              </p>
            </div>
            <div>
              <Mark>The audit trail</Mark>
              <h2 className="mt-3 max-w-[22ch] font-serif text-3xl leading-tight md:text-4xl">
                When an item is challenged, the chain is the answer.
              </h2>
              <p className="mt-5 leading-relaxed text-slate">
                Every published item traces from the designated source passage through generation, triage, expert
                review, and approval to its published form, with names and dates. A hallucinated source span is treated
                as fabricated provenance and rejected. A challenge is answered from the record, not from recollection.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* Validation evidence */}
      <section id="evidence" className="border-y border-hairline bg-ink-deep/40 py-20 md:py-28">
        <Container>
          <Mark>Validation evidence</Mark>
          <h2 className="mt-3 max-w-[26ch] font-serif text-3xl leading-tight md:text-4xl">
            Meets or exceeds traditional psychometric benchmarks.
          </h2>
          <div className="mt-10 overflow-hidden rounded-media border border-hairline">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-hairline bg-surface">
                  <th scope="col" className="px-5 py-3 font-semibold text-bright">Metric</th>
                  <th scope="col" className="px-5 py-3 font-semibold text-seal">2025 production</th>
                  <th scope="col" className="px-5 py-3 font-semibold text-slate">Benchmark</th>
                </tr>
              </thead>
              <tbody>
                {psychometrics.map((row) => (
                  <tr key={row.metric} className="border-b border-hairline last:border-0">
                    <td className="px-5 py-3 text-slate">{row.metric}</td>
                    <td className="px-5 py-3 font-semibold text-bright">{row.edai}</td>
                    <td className="px-5 py-3 text-faint">{row.benchmark}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-14 grid gap-10 lg:grid-cols-[1fr_1.3fr] lg:gap-16">
            <div className="rounded-media border border-seal/40 bg-surface p-7 self-start">
              <p className="font-serif text-3xl text-bright">68.65%</p>
              <p className="mt-2 font-serif text-xl text-seal">{proofStudy.headline}</p>
            </div>
            <div>
              <p className="leading-relaxed text-slate">{proofStudy.body}</p>
              <p className="mt-4 border-l-2 border-seal pl-4 font-medium leading-relaxed text-bright">
                {proofStudy.implication}
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* The corpus underneath */}
      <section className="py-20 md:py-28">
        <Container>
          <Mark>The corpus underneath</Mark>
          <h2 className="mt-3 max-w-[24ch] font-serif text-3xl leading-tight md:text-4xl">
            Nothing uncertified is ever served.
          </h2>
          <p className="mt-5 max-w-measure-wide leading-relaxed text-slate">{corpus.intro}</p>
          <dl className="mt-10 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {corpus.stats.map((s) => (
              <div key={s.label}>
                <dd className="font-serif text-4xl text-bright">{s.value}</dd>
                <dt className="mt-2 max-w-[26ch] text-sm leading-relaxed text-slate">{s.label}</dt>
              </div>
            ))}
          </dl>
        </Container>
      </section>

      {/* Modules with screenshots */}
      <section className="border-y border-hairline bg-ink-deep/40 py-20 md:py-28">
        <Container>
          <Mark>Inside the suite</Mark>
          <h2 className="mt-3 max-w-[26ch] font-serif text-3xl leading-tight md:text-4xl">
            The working infrastructure of a certifying board.
          </h2>
          <div className="mt-12 grid gap-10 lg:grid-cols-[1fr_1.3fr] lg:gap-14">
            <div className="space-y-1">
              {modules.map((m, i) => (
                <button
                  key={m.name}
                  type="button"
                  onClick={() => setActiveModule(i)}
                  aria-pressed={i === activeModule}
                  className={`block w-full rounded border px-4 py-3 text-left transition-colors ${
                    i === activeModule
                      ? 'border-seal/50 bg-surface'
                      : 'border-transparent hover:border-hairline hover:bg-surface/60'
                  }`}
                >
                  <span className={`text-sm font-semibold ${i === activeModule ? 'text-bright' : 'text-slate'}`}>
                    {m.name}
                  </span>
                </button>
              ))}
            </div>
            <div>
              <h3 className="font-serif text-2xl text-bright">{mod.name}</h3>
              <p className="mt-3 leading-relaxed text-slate">{mod.text}</p>
              {mod.shot ? (
                <figure className="mt-6">
                  <img
                    src={mod.shot.src}
                    alt={mod.shot.alt}
                    loading="lazy"
                    className="w-full rounded-media border border-hairline bg-white"
                  />
                  <figcaption className="mt-2 text-sm text-faint">{mod.shot.caption}</figcaption>
                </figure>
              ) : (
                <p className="mt-6 rounded-media border border-hairline bg-surface px-5 py-4 text-sm text-faint">
                  Screenshots for this module are being prepared.
                </p>
              )}
            </div>
          </div>
        </Container>
      </section>

      {/* QA pipeline */}
      <section className="py-20 md:py-28">
        <Container>
          <Mark>Quality assurance</Mark>
          <h2 className="mt-3 max-w-[24ch] font-serif text-3xl leading-tight md:text-4xl">
            Five gates between a draft and an exam.
          </h2>
          <ol className="mt-12 space-y-4 lg:max-w-measure-wide">
            {qaPipeline.map((gate, i) => (
              <li key={gate.name} className="flex gap-5 rounded-media border border-hairline bg-surface p-6">
                <span
                  aria-hidden="true"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-seal/60 font-serif text-sm text-seal"
                >
                  {i + 1}
                </span>
                <div>
                  <h3 className="font-semibold text-bright">{gate.name}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate">{gate.text}</p>
                </div>
              </li>
            ))}
          </ol>
          <p className="mt-8 max-w-measure-wide border-l-2 border-seal pl-4 text-sm leading-relaxed text-slate">
            {qaBar}
          </p>
        </Container>
      </section>

      {/* Video slot */}
      <section className="border-y border-hairline bg-ink py-20 text-paper-dark-text md:py-28">
        <Container>
          <Mark dark>See it</Mark>
          <h2 className="mt-3 font-serif text-3xl leading-tight md:text-4xl">{video.title}</h2>
          <p className="mt-4 max-w-measure leading-relaxed text-slate-dark-text">{video.text}</p>
          <div className="mt-10 lg:max-w-measure-wide">
            <VideoSlot
              video={pipelineVideo}
              title={video.title}
              text="A pipeline walkthrough film is planned; request a live demo in the meantime."
            />
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28">
        <Container>
          <Mark>Questions boards ask</Mark>
          <h2 className="mt-3 font-serif text-3xl leading-tight md:text-4xl">Asked and answered.</h2>
          <div className="mt-10 max-w-measure-wide divide-y divide-hairline border-y border-hairline">
            {faq.map((item, i) => (
              <div key={item.q}>
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                  className="flex w-full items-baseline justify-between gap-6 py-5 text-left"
                >
                  <span className="font-semibold text-bright">{item.q}</span>
                  <span aria-hidden="true" className="text-seal">{openFaq === i ? '\u2212' : '+'}</span>
                </button>
                {openFaq === i && <p className="pb-6 leading-relaxed text-slate">{item.a}</p>}
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Closing CTA */}
      <section className="border-t border-hairline bg-ink-deep/40 py-20">
        <Container className="text-center">
          <h2 className="mx-auto max-w-[26ch] font-serif text-3xl leading-tight md:text-4xl">
            Your sources. Your blueprint. Your exam, defensible.
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-primary">Talk to us about your board</Link>
            <Link to="/who-its-for" className="btn-secondary">Who the suite serves</Link>
          </div>
        </Container>
      </section>
    </>
  );
}
